// File: webelieve-frontend/src/features/intake/components/PublicFormPage.tsx
// EF-6.5: Public-facing form renderer — standalone page, no auth

import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import type { FormSchema, FormField, FormFieldType } from '../../admin/components/settings/intake-forms/intakeFormApi';

interface PublicFormData {
  form_id: string;
  form_name: string;
  description?: string;
  form_schema: FormSchema;
  realm_id: string;
}

type FormValues = Record<string, any>;

interface FileEntry { _file: true; name: string; size: number; _fileObj: File }

const MAX_FILES_PER_FIELD = 5;
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isFieldVisible(field: FormField, values: FormValues): boolean {
  if (!field.conditional_logic) return true;
  const { show_if_field, show_if_value } = field.conditional_logic;
  return String(values[show_if_field] ?? '') === String(show_if_value);
}

function renderLiveField(
  field: FormField,
  value: any,
  onChange: (val: any) => void,
): React.ReactNode {
  const base =
    'w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-lex-accent focus:border-lex-accent outline-none';

  switch (field.type as FormFieldType) {
    case 'text':
    case 'email':
    case 'phone':
    case 'number':
      return (
        <input
          type={field.type === 'phone' ? 'tel' : field.type}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          className={base}
        />
      );

    case 'currency':
      return (
        <div className="relative">
          <span className="absolute left-3 top-2 text-sm text-gray-400">$</span>
          <input
            type="number"
            step="0.01"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder || '0.00'}
            className={base + ' pl-7'}
          />
        </div>
      );

    case 'textarea':
      return (
        <textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          rows={4}
          className={base + ' resize-y'}
        />
      );

    case 'date':
      return (
        <input
          type="date"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className={base}
        />
      );

    case 'select':
      return (
        <select value={value || ''} onChange={(e) => onChange(e.target.value)} className={base}>
          <option value="">{field.placeholder || 'Select an option...'}</option>
          {(field.options || []).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );

    case 'multiselect': {
      const selected: string[] = Array.isArray(value) ? value : [];
      return (
        <div className="space-y-1.5">
          {(field.options || []).map((opt) => (
            <label key={opt.value} className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={selected.includes(opt.value)}
                onChange={(e) => {
                  const next = e.target.checked
                    ? [...selected, opt.value]
                    : selected.filter((v) => v !== opt.value);
                  onChange(next);
                }}
                className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
              />
              {opt.label}
            </label>
          ))}
        </div>
      );
    }

    case 'checkbox':
      return (
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
            className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
          />
          {field.placeholder || field.label}
        </label>
      );

    case 'file': {
      const fileList: FileEntry[] = Array.isArray(value) ? value : [];
      const atLimit = fileList.length >= MAX_FILES_PER_FIELD;
      return (
        <div className="space-y-2">
          <input
            type="file"
            multiple
            disabled={atLimit}
            onChange={(e) => {
              const incoming = Array.from(e.target.files || []);
              const next = [...fileList];
              for (const file of incoming) {
                if (next.length >= MAX_FILES_PER_FIELD) break;
                if (file.size > MAX_FILE_SIZE) {
                  alert(`"${file.name}" exceeds the 50 MB limit and was skipped.`);
                  continue;
                }
                // Skip duplicates by name+size
                if (next.some((f) => f.name === file.name && f.size === file.size)) continue;
                next.push({ _file: true, name: file.name, size: file.size, _fileObj: file });
              }
              onChange(next.length > 0 ? next : '');
              e.target.value = ''; // Reset so same file can be re-selected
            }}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-lex-accent-soft file:text-lex-accent hover:file:bg-lex-state-hover disabled:opacity-40"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp,.zip,.mp4,.mov,.mp3,.wav"
          />
          <p className="text-[11px] text-gray-400">
            Up to {MAX_FILES_PER_FIELD} files, 50 MB each. PDF, DOCX, XLSX, images, ZIP.
          </p>
          {fileList.length > 0 && (
            <ul className="space-y-1">
              {fileList.map((f, i) => (
                <li key={`${f.name}-${i}`} className="flex items-center justify-between px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded text-sm">
                  <span className="truncate text-gray-700 mr-2">{f.name} <span className="text-gray-400">({formatBytes(f.size)})</span></span>
                  <button
                    type="button"
                    onClick={() => {
                      const next = fileList.filter((_, idx) => idx !== i);
                      onChange(next.length > 0 ? next : '');
                    }}
                    className="flex-shrink-0 text-red-400 hover:text-red-600 text-xs font-medium"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      );
    }

    case 'address':
      return (
        <div className="space-y-2">
          <input
            type="text"
            value={value?.street || ''}
            onChange={(e) => onChange({ ...value, street: e.target.value })}
            placeholder="Street address"
            className={base}
          />
          <div className="grid grid-cols-3 gap-2">
            <input
              type="text"
              value={value?.city || ''}
              onChange={(e) => onChange({ ...value, city: e.target.value })}
              placeholder="City"
              className={base}
            />
            <input
              type="text"
              value={value?.state || ''}
              onChange={(e) => onChange({ ...value, state: e.target.value })}
              placeholder="State"
              className={base}
            />
            <input
              type="text"
              value={value?.zip || ''}
              onChange={(e) => onChange({ ...value, zip: e.target.value })}
              placeholder="ZIP"
              className={base}
            />
          </div>
        </div>
      );

    default:
      return (
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className={base}
        />
      );
  }
}

const API_BASE = import.meta.env.VITE_API_URL || '';

const PublicFormPage: React.FC = () => {
  const { formId } = useParams<{ formId: string }>();
  const [formData, setFormData] = useState<PublicFormData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [values, setValues] = useState<FormValues>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const startTimeRef = useRef(Date.now());

  useEffect(() => {
    if (!formId) return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/v1/public/intake/forms/${formId}`);
        if (!res.ok) {
          const body = await res.json().catch(() => null);
          throw new Error(body?.error || `Form not found (${res.status})`);
        }
        const json = await res.json();
        setFormData(json.data);
      } catch (err: any) {
        setError(err.message || 'Failed to load form');
      } finally {
        setLoading(false);
      }
    })();
  }, [formId]);

  const validate = (): boolean => {
    if (!formData) return false;
    const errs: Record<string, string> = {};
    for (const field of formData.form_schema.fields) {
      if (!isFieldVisible(field, values)) continue;
      if (field.required) {
        const v = values[field.id];
        let empty: boolean;
        if (field.type === 'file') {
          // File fields store an array of FileEntry objects
          empty = !Array.isArray(v) || v.length === 0;
        } else {
          empty =
            v === undefined ||
            v === null ||
            v === '' ||
            (Array.isArray(v) && v.length === 0) ||
            (typeof v === 'object' && !Array.isArray(v) && Object.values(v).every((x) => !x));
        }
        if (empty) {
          errs[field.id] = field.validation?.message || `${field.label} is required`;
        }
      }
      if (field.validation?.pattern && values[field.id]) {
        try {
          if (!new RegExp(field.validation.pattern).test(String(values[field.id]))) {
            errs[field.id] = field.validation.message || `${field.label} is invalid`;
          }
        } catch { /* ignore bad regex */ }
      }
    }
    setValidationErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || !formData) return;

    setSubmitting(true);
    try {
      // Separate file objects from plain form values
      const files: Array<{ fieldId: string; file: File }> = [];
      const cleanResponses: Record<string, any> = {};
      for (const [key, val] of Object.entries(values)) {
        if (Array.isArray(val) && val.length > 0 && val[0]?._file) {
          // Multi-file field — collect all File objects
          const names: string[] = [];
          for (const entry of val as FileEntry[]) {
            if (entry._fileObj instanceof File) {
              files.push({ fieldId: key, file: entry._fileObj });
              names.push(entry.name);
            }
          }
          cleanResponses[key] = names.join(', ');
        } else {
          cleanResponses[key] = val;
        }
      }

      const metadata = {
        completion_time_seconds: Math.round((Date.now() - startTimeRef.current) / 1000),
        referrer: document.referrer || undefined,
      };

      let res: Response;
      if (files.length > 0) {
        // Use FormData for multipart upload
        const fd = new FormData();
        fd.append('form_responses', JSON.stringify(cleanResponses));
        fd.append('metadata', JSON.stringify(metadata));
        for (const { fieldId, file } of files) {
          fd.append('files', file, file.name);
          fd.append('file_field_ids', fieldId);
        }
        res = await fetch(`${API_BASE}/api/v1/public/intake/forms/${formId}/submit`, {
          method: 'POST',
          body: fd, // No Content-Type header — browser sets multipart boundary
        });
      } else {
        res = await fetch(`${API_BASE}/api/v1/public/intake/forms/${formId}/submit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ form_responses: cleanResponses, metadata }),
        });
      }

      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error || 'Submission failed');
      }
      setSubmitted(true);
    } catch (err: any) {
      alert(err.message || 'Failed to submit form');
    } finally {
      setSubmitting(false);
    }
  };

  // --- Loading state ---
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lex-accent" />
      </div>
    );
  }

  // --- Error state ---
  if (error || !formData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center max-w-md">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Form Not Available</h1>
          <p className="text-sm text-gray-500">{error || 'This form could not be loaded.'}</p>
        </div>
      </div>
    );
  }

  // --- Success state ---
  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center max-w-md">
          <div className="mx-auto h-14 w-14 bg-lex-success-bg rounded-full flex items-center justify-center mb-4">
            <svg className="h-7 w-7 text-lex-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Thank You!</h1>
          <p className="text-sm text-gray-500">
            {formData.form_schema.success_message || 'Your submission has been received. We will be in touch shortly.'}
          </p>
        </div>
      </div>
    );
  }

  // --- Form ---
  const schema = formData.form_schema;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* Header */}
          <div className="p-6 border-b border-gray-200">
            <h1 className="text-xl font-semibold text-gray-900">{schema.title || formData.form_name}</h1>
            {schema.description && (
              <p className="text-sm text-gray-500 mt-1">{schema.description}</p>
            )}
          </div>

          {/* Fields */}
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {schema.fields.map((field) => {
              if (!isFieldVisible(field, values)) return null;
              return (
                <div key={field.id}>
                  {field.type !== 'checkbox' && (
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {field.label}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                  )}
                  {renderLiveField(field, values[field.id], (val) =>
                    setValues((prev) => ({ ...prev, [field.id]: val })),
                  )}
                  {validationErrors[field.id] && (
                    <p className="text-xs text-red-500 mt-1">{validationErrors[field.id]}</p>
                  )}
                </div>
              );
            })}

            <button
              type="submit"
              disabled={submitting}
              className="w-full px-4 py-2.5 bg-lex-accent text-lex-accent-contrast text-sm font-medium rounded-md hover:bg-lex-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? 'Submitting...' : schema.submit_button_text || 'Submit'}
            </button>
          </form>
        </div>

        <p className="text-[10px] text-gray-400 text-center mt-4">
          Powered by EdgeLex
        </p>
      </div>
    </div>
  );
};

export default PublicFormPage;
